import type { InstructionsDirectory, InstructionsFile, InstructionsTree } from '@/redux/slices/Doctors';

type ApiDirectoryFile = {
    fileId: string;
    seqId: number;
    directoryId: string;
    fileType: string;
    fileName: string;
    fileSize: number;
    fileUrl: string;
    fileMetadata: unknown | null;
    createdAt: string | Date;
    updatedAt: string | Date;
};

type ApiDirectoryNode = {
    directoryId: string;
    seqId: number;
    name: string;
    isRootDirectory: boolean;
    parentDirectoryId: string | null;
    createdAt: string | Date;
    updatedAt: string | Date;
    children: ApiDirectoryNode[];
    files: ApiDirectoryFile[];
};

export type DoctorDirectoryTreeResponse = {
    tree: ApiDirectoryNode[];
};

const normalizeDate = (value: string | Date): string => {
    if (typeof value === 'string') return value;
    return value.toISOString();
};

export const flattenDirectoryTree = (nodes: ApiDirectoryNode[] | undefined | null): InstructionsTree => {
    const allDirs: InstructionsDirectory[] = [];
    const childrenByParent: Record<string, InstructionsDirectory[]> = {};
    const filesByDirectoryId: Record<string, InstructionsFile[]> = {};

    const walk = (list: ApiDirectoryNode[], parentId: string | null) => {
        for (const node of list) {
            const dir: InstructionsDirectory = {
                directoryId: node.directoryId,
                name: node.name,
                isRootDirectory: node.isRootDirectory,
                parentDirectoryId: parentId,
                createdAt: normalizeDate(node.createdAt),
                updatedAt: normalizeDate(node.updatedAt),
                seqId: node.seqId,
            };

            allDirs.push(dir);
            const key = parentId ?? '__root';
            if (!childrenByParent[key]) childrenByParent[key] = [];
            childrenByParent[key].push(dir);

            const files: InstructionsFile[] = (node.files ?? []).map((f) => ({
                fileId: f.fileId,
                seqId: f.seqId,
                directoryId: f.directoryId,
                fileName: f.fileName,
                fileType: f.fileType,
                fileSize: f.fileSize,
                fileUrl: f.fileUrl,
                fileMetadata: f.fileMetadata ?? undefined,
                createdAt: normalizeDate(f.createdAt),
                updatedAt: normalizeDate(f.updatedAt),
            }));
            filesByDirectoryId[node.directoryId] = files;

            walk(node.children ?? [], node.directoryId);
        }
    };

    walk(nodes ?? [], null);

    const roots = childrenByParent['__root'] ?? allDirs.filter((d) => d.parentDirectoryId === null);

    return {
        allDirs,
        roots,
        childrenByParent,
        filesByDirectoryId,
    };
};

export const findById = (dirs: InstructionsDirectory[], directoryId: string): InstructionsDirectory | undefined =>
    dirs.find((d) => d.directoryId === directoryId);

export const buildPathIds = (dirs: InstructionsDirectory[], directoryId: string): string[] => {
    const path: string[] = [];
    let current: InstructionsDirectory | undefined = findById(dirs, directoryId);

    while (current) {
        path.unshift(current.directoryId);
        current = current.parentDirectoryId ? findById(dirs, current.parentDirectoryId) : undefined;
    }

    return path;
};

export const buildBreadcrumbs = (dirs: InstructionsDirectory[], pathIds: string[]): InstructionsDirectory[] =>
    pathIds
        .map((id) => findById(dirs, id))
        .filter((d): d is InstructionsDirectory => d !== undefined);

